import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { useLanguage } from "../../context/LanguageContext";
import { B2C_ENABLED } from "../../config/features";

// FAQ entries flagged with `pricing: true` are hidden when B2C payments are disabled.

const translations = {
  overline: { es: "FAQ", en: "FAQ" },
  title: { es: "¿Todavía tienes dudas?", en: "Still Have Questions?" },
  subtitle: {
    es: "Resolvemos las preguntas más comunes sobre nuestra academia y cursos para que avances con confianza.",
    en: "We answer the most common questions about our academy and courses so you can move forward with confidence.",
  },
  cta: { es: B2C_ENABLED ? "Ver cursos disponibles" : "Ver cursos", en: B2C_ENABLED ? "View Available Courses" : "View Courses" },
  faqs: {
    es: [
      { question: "¿A quiénes está dirigida esta formación?", answer: "A organizaciones de la industria MICE en Latinoamérica: Convention Bureaus, destinos, cadenas hoteleras, recintos, DMCs, agencias y corporativos que quieran profesionalizar a sus equipos con formación especializada." },
      { question: "¿Cómo se imparten los programas?", answer: "Diseñamos cada programa a la medida del cliente. Pueden ser presenciales, virtuales en vivo o formato mixto, adaptándonos a las agendas y objetivos de tu equipo." },
      { question: "¿Se necesita experiencia previa en la industria?", answer: "No. Nuestros programas se ajustan al nivel del equipo. Podemos formar tanto a personas que están iniciando en la industria como a profesionales con experiencia que buscan especializarse." },
      { question: "¿Los participantes reciben certificado?", answer: "Sí. Al finalizar el programa, cada participante recibe un certificado de finalización que respalda su formación en el área trabajada." },
      { question: "¿Cuál es el valor de referencia de un programa?", answer: "El valor de referencia es de USD 3,600 hasta para 30 personas. El precio final se ajusta según la duración, formato, contenido personalizado y número de participantes." },
      { question: "¿Los participantes reciben materiales de referencia?", answer: "Sí. Al finalizar el programa entregamos materiales digitales, guías y recursos prácticos para reforzar y aplicar lo aprendido en el puesto de trabajo." },
      { question: "¿Estos programas están alineados con estándares internacionales?", answer: "Sí. Nuestros programas están alineados con el marco educativo global de PCMA Institute y las mejores prácticas de la industria de reuniones a nivel mundial." },
      { question: "¿En qué idiomas se pueden impartir los programas?", answer: "Nuestros programas se imparten principalmente en español, con opción bilingüe (español–inglés) según las necesidades del cliente." },
    ],
    en: [
      { question: "Who is this training for?", answer: "For MICE industry organizations in Latin America: Convention Bureaus, destinations, hotel chains, venues, DMCs, agencies, and corporates that want to professionalize their teams with specialized training." },
      { question: "How are the programs delivered?", answer: "We design each program tailored to the client. They can be delivered in-person, live virtual, or in a blended format, adapting to your team's schedule and objectives." },
      { question: "Do participants need previous industry experience?", answer: "No. Our programs adjust to the team's level. We can train people just starting in the industry as well as experienced professionals looking to specialize." },
      { question: "Do participants receive a certificate?", answer: "Yes. Upon completing the program, each participant receives a completion certificate that supports their training in the specific area covered." },
      { question: "What is the reference investment for a program?", answer: "The reference investment is USD 3,600 for up to 30 participants. The final price adjusts based on duration, format, custom content, and number of participants." },
      { question: "Do participants receive reference materials?", answer: "Yes. Upon completing the program we deliver digital materials, guides, and practical resources to reinforce and apply what has been learned in the workplace." },
      { question: "Are these programs aligned with international standards?", answer: "Yes. Our programs are aligned with PCMA Institute's global education framework and the best practices of the meetings industry worldwide." },
      { question: "In which languages can the programs be delivered?", answer: "Our programs are delivered primarily in Spanish, with a bilingual (Spanish–English) option available based on client needs." },
    ],
  },
};

const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const faqs = t(translations.faqs).filter((f) => B2C_ENABLED || !f.pricing);

  const scrollToCourses = () => {
    document.querySelector('#cursos')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="faq" data-testid="faq-section" ref={ref} className="py-24 md:py-32 bg-[#EEF1F4]">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-12 md:mb-16">
          <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(translations.overline)}
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
          </span>
          <h2 data-testid="faq-title" className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-6">
            {t(translations.title)}
          </h2>
          <p className="font-body text-[#0B1F3B]/70 text-lg max-w-xl mx-auto">{t(translations.subtitle)}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }}>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} data-testid={`faq-item-${index}`} className="bg-white rounded-xl border border-gray-200 px-6 overflow-hidden">
                <AccordionTrigger data-testid={`faq-trigger-${index}`} className="font-body text-left text-[#0B1F3B] font-medium text-base md:text-lg py-5 hover:no-underline hover:text-[#2ED3C6] transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${index}`} className="font-body text-[#0B1F3B]/70 text-base pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.6 }} className="text-center mt-12">
          <button
            onClick={scrollToCourses}
            data-testid="faq-cta"
            className="inline-flex items-center gap-3 bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-4 text-lg transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[#F59E0B]/30 group"
          >
            {t(translations.cta)}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
